<script lang="ts">
	import { DataHandlerDevice, type DeviceGroup, type Scene } from "luxedo-data"
	import { Responsive } from "responsive"
	import { RadioSelector } from "misc-ui"

	type Props = {
		scene: Scene
		slotSrc: { [index: number]: string }
		group: DeviceGroup
		onFinish: () => void
	}

	let { group, slotSrc, onFinish, scene }: Props = $props()

	type SlotId = number | "all"

	let isMobile = $derived(Responsive.isMobile)
	let selected: SlotId = $state()

	let downloadOptions = $derived.by(() => {
		const childOpts = Object.keys(slotSrc).map((id) => ({
			title: getDeviceInfo(Number(id)).name,
			color: getDeviceInfo(Number(id)).color,
			value: id,
		}))

		return [
			{
				title: "Whole Scene",
				value: "all",
				color: "var(--color-text-light)",
			},
			...childOpts,
		]
	})

	function getDeviceInfo(slotId: number) {
		const slot = group.slots.find((slot) => slot.id == slotId)
		const devId = slot.device_id
		const device = DataHandlerDevice.get(devId)
		if (device) return { color: device.color, name: device.name }
		else return { color: "--color-main", name: "Unknown" }
	}

	const beginDownloads = async () => {
		if (selected === "all") await scene.triggerDownload()
		else {
			const slotId = Number(selected)
			const deviceName = getDeviceInfo(slotId).name
			await triggerDownload(slotSrc[slotId], deviceName)
		}

		if (onFinish) onFinish()
	}

	const triggerDownload = async (src: string, deviceName: string) => {
		return new Promise<void>((res) => {
			const link = document.createElement("a")
			link.target = "_blank"
			link.href = src
			link.style.display = "none"
			link.download = `${deviceName}.mp4`

			document.body.appendChild(link)
			link.click()

			setTimeout(() => {
				link.parentNode?.removeChild(link)
				res()
			}, 250)
		})
	}

	function onChange(val: SlotId) {
		selected = val
	}
</script>

<div class="scene-chunk-selector" class:isMobile>
	<p>
		Select which device's perspective to download, or select "whole scene" to download the entire
		show uncropped.
	</p>

	<RadioSelector
		name="scene-downloader-input"
		onChange={(val) => onChange(val as SlotId)}
		options={downloadOptions}
		value={selected}
	/>
</div>

<div class="button-container" class:isMobile>
	<button onclick={beginDownloads} disabled={selected == undefined}>Download</button>
</div>

<style>
	.button-container {
		width: fit-content;
		position: absolute;
		bottom: 0;
		right: 1rem;
	}

	.scene-chunk-selector p {
		max-width: 40dvw;
		color: var(--color-text);
		line-height: 1.25em;
	}

	.scene-chunk-selector {
		margin-bottom: 1rem;
	}

	.scene-chunk-selector :global(svg) {
		width: 2rem;
		margin-right: 0.5rem;
	}

	.scene-chunk-selector :global(label) {
		display: flex;
		flex-direction: row;
		align-items: center;
	}

	.scene-chunk-selector.isMobile {
		margin: 2rem 0;
	}

	.isMobile.scene-chunk-selector p {
		max-width: unset;
	}

	.isMobile button {
		font-size: var(--h3);
	}

	.scene-chunk-selector :global(svg) {
		width: auto;
		height: 1rem;
		margin-right: 0.5rem;
	}
</style>
